<template>
  <d-card v-if="applicant">
    <v-form
      ref="form"
      :data="form"
      format="formData"
      :url="$laravel.route('public.applicant.update', {
        applicant: applicant.id,
      })"
      method="post"
      @success="onSuccess"
    >
      <template #default="{ errors }">
        <d-card-row>
          <v-input-select
            v-model="form.salary_type"
            name="salary_type"
            :label="$t('salary_type')"
            required
          >
            <option :value="null">
              {{ $t("select").capitalize() }}
            </option>
            <option
              v-for="(salaryType, key) in $laravel.applicantSalaryTypeEnum"
              :key="key"
              :value="salaryType.value"
            >
              {{ salaryType.label }}
            </option>
          </v-input-select>
          <d-notification
            v-if="notificationItems.length"
            type="is-info is-light"
          >
            <div class="has-list">
              <div
                v-for="(item, key) in notificationItems"
                :key="key"
              >
                <div v-html="item" />
              </div>
            </div>
          </d-notification>
        </d-card-row>
        <d-card-row v-if="form.salary_type">
          <v-input-select
            v-if="form.salary_type === 'retired'"
            v-model="form.salary_type_retired_document_type"
            name="salary_type_retired_document_type"
            :label="$t('salary_type_retired_document_type')"
            required
          >
            <option :value="null">
              {{ $t("select").capitalize() }}
            </option>
            <option
              v-for="(documentType, key) in salaryTypeRetiredDocumentType"
              :key="key"
              :value="documentType.value"
            >
              {{ documentType.label }}
            </option>
          </v-input-select>
        </d-card-row>
        <d-card-row>
          <div
            v-if="isShownSalaryStatementFiles"
            class="mb-4"
          >
            <v-input-media
              v-model="form.salary_statement_files"
              :existing-files="applicant.salary_statement_files"
              name="salary_statement_files"
              :label="$t('salary_statement_files')"
              :max="3"
              required
            />
          </div>
        </d-card-row>
        <d-card-row>
          <div
            v-if="isShownBankStatementFiles"
            class="mb-4"
          >
            <v-input-media
              v-model="form.bank_statement_files"
              :existing-files="applicant.bank_statement_files"
              name="bank_statement_files"
              :label="$t('bank_statement_files')"
              :max="3"
              required
            />
          </div>
        </d-card-row>
        <d-card-row>
          <div
            v-if="isShownEmploymentContract"
            class="mb-4"
          >
            <v-input-media
              v-model="form.employment_contract"
              :existing-files="applicant.employment_contract"
              name="employment_contract"
              :label="$t('employment_contract')"
              :max="1"
              required
            />
          </div>
        </d-card-row>

        <d-card-row>
          <div
            v-if="isShownDepositNotice"
            class="mb-4"
          >
            <v-input-media
              v-model="form.deposit_notice"
              :existing-files="applicant.deposit_notice"
              name="deposit_notice"
              :label="$t('deposit_notice')"
              :max="1"
              required
            />
          </div>
        </d-card-row>

        <d-card-row>
          <div
            v-if="isShownNoticeOfAssessmentFile"
            class="mb-4"
          >
            <div class="mb-4">
              <v-input-switch
                v-model="form.has_notice_of_assessment"
                :true-value="true"
                :false-value="false"
                name="has_notice_of_assessment"
                :label="$t('has_notice_of_assessment')"
              />
            </div>
            <div
              v-if="! form.has_notice_of_assessment"
              class="mb-4"
            >
              <v-input-media
                v-model="form.notice_of_assessment_file"
                :existing-files="applicant.notice_of_assessment_file"
                name="notice_of_assessment_file"
                :label="$t('notice_of_assessment_file')"
                :max="1"
                required
              />
            </div>
          </div>
        </d-card-row>

        <d-card-row>
          <button
            class="button is-primary"
            type="submit"
          >
            {{ $t('submit_application') }}
          </button>
        </d-card-row>
      </template>
    </v-form>
  </d-card>
</template>


<script>
import { fillFields } from "@/services/helpers";
import { i18n } from "@/services/i18n";

export default {
  props: {
    applicant: {
      default: null,
      type: Object
    }
  },
  data () {
    return {
      form: {
        salary_type: null,
        bank_statement_files:[],
        employment_contract: null,
        salary_statement_files: [],
        deposit_notice: null,
        notice_of_assessment_file: null,
        has_notice_of_assessment: false,
        salary_type_retired_document_type: null,
        step: "finance",
      }
    };
  },
  computed: {
    isShownSalaryStatementFiles(){
      return this.form.salary_type == "salary_15_days_or_more";
    },
    isShownBankStatementFiles(){
      if (this.form.salary_type == "retired") {
        return this.form.salary_type_retired_document_type == "bank_statement_files";
      }

      return this.form.salary_type == "self_employed" ||
             this.form.salary_type == "unemployed";
    },
    isShownEmploymentContract(){
      return this.form.salary_type == "salary_less_15_days";
    },
    isShownDepositNotice(){
      return this.form.salary_type == "retired" && this.form.salary_type_retired_document_type == "deposit_notice";
    },
    isShownNoticeOfAssessmentFile(){
      return this.form.salary_type == "self_employed" ||
             this.form.salary_type == "retired";
    },
    notificationItems() {

      if (this.form.salary_type === "salary_15_days_or_more") {
        return [
          i18n.t("salary_type_notifications_salary_15_days_or_more"),
        ];
      }

      if (this.form.salary_type === "salary_less_15_days") {
        return [
          i18n.t("salary_type_notifications_salary_less_15_days"),
        ];
      }

      if (this.form.salary_type === "self_employed") {
        return [
          i18n.t("salary_type_notifications_self_employed"),
        ];
      }

      if (this.form.salary_type === "retired") {
        return [
          i18n.t("salary_type_notifications_retired"),
        ];
      }

      if (this.form.salary_type === "unemployed") {
        return [
          i18n.t("salary_type_notifications_unemployed"),
        ];
      }

      return [];
    },
    salaryTypeRetiredDocumentType() {
      return [
        {
          value: "bank_statement_files",
          label: this.$t("bank_statement_files")
        },
        {
          value: "deposit_notice",
          label: this.$t("deposit_notice")
        },
      ];
    }
  },
  created () {
    this.form = fillFields(this.form, this.applicant);
  },
  methods: {
    onSuccess() {
      this.$emit("success");
    },
  }
};
</script>

<style>
.has-list ul {
  list-style-type: disc !important;
  padding-left: 20px;
}

.has-list ul li {
  margin-top: 5px;
}
.has-list ol {
  list-style-position: inside !important;
}
.has-list ol li {
  margin-top: 5px;
}
</style>
